<template>
  <VDialogV3
    ref="tenderBidCard"
    id="create-tender-bid"
    position="right"
    not-hide-on-click
    width="600px"

    :entity="localBid"
    :tabs="tabs"
    v-model:active-tab="activeTab"
    :context-menu="TenderBidsService.contextMenu"

    @submit="submit"
  >
    <!-- v-show используется специально, чтобы мы смогли провалидировать каждую вкладку.
     если писать v-if контролы не смонтируются и мы не сможем их провалидировать -->
    <fieldset v-show="activeTab.id === 'primary'" data-tab-id="primary">
      <CreateTenderBidPrimary :bid="localBid"/>
    </fieldset>

    <fieldset v-show="activeTab.id === 'secondary'" data-tab-id="secondary">
      <CreateTenderBidSecondary :bid="localBid"/>
    </fieldset>

    <fieldset v-show="activeTab.id === 'characteristic'" data-tab-id="characteristic">
      <OfferCharacteristicSection
        :id="localBid.offer.id"
        :product="localBid.tender.product"
      />
    </fieldset>

    <template #footer>
      <VButton
        v-if="$core.currentPartner.isBuyer === true"
        theme="primary"
        :disabled="localBid.tender.getReasonCannotCreateOrderByCurrentUser() !== null"
        :title="localBid.tender.getReasonCannotCreateOrderByCurrentUser()"
        @click="openBuyDialog"
      >
        Создать {{ localBid.tender.isPreorderNeeded === true ? 'предзаказ' : 'заказ' }}
      </VButton>

      <VButton
        v-if="$core.currentPartner.isSeller === true"
        theme="primary"
        :disabled="localBid.tender.getReasonCannotCreateBidByCurrentUser() !== null"
        @mouseenter="$core.tooltip.show($event.currentTarget, localBid.tender.getReasonCannotCreateBidByCurrentUser())"
        @mouseleave="$core.tooltip.hide()"
        @click="() => $refs.tenderBidCard?.onSubmitButtonClicked()"
      >
        Сделать ставку
      </VButton>

      <div
        v-if="$core.utils.isNotEmptyArray(localBid.getMismatches(localBid.tender))"
        class="warning-wrapper cursor-pointer"
        @click="$core.dialogs.TenderBidMismatchDialog.show({ tenderBid: localBid })"
      >
        <div class="attention-dot"/>
        В <span v-if="localBid.isMine === true">&nbsp;вашей&nbsp;</span> ставке есть расхождения
      </div>

      <VButton @click="$core.dialogs.TenderBidCard.hide()">Закрыть</VButton>
    </template>
  </VDialogV3>
</template>

<style lang="scss" src="./TenderBidCard.scss"/>

<script lang="ts">
import type Tender from '@/domains/Tenders/Tender';
import OffersService from '@/domains/Offers/OffersService';
import TendersService from '@/domains/Tenders/TendersService';
import TenderBid from '@/domains/TenderBids/TenderBid';
import TenderBidsService from '@/domains/TenderBids/TenderBidsService';

import CreateTenderBidPrimary
  from '@/domains/TenderBids/components/CreateTenderBid/CreateTenderBidPrimary/CreateTenderBidPrimary.vue';
import CreateTenderBidSecondary
  from '@/domains/TenderBids/components/CreateTenderBid/CreateTenderBidSecondary/CreateTenderBidSecondary.vue';
import OfferCharacteristicSection
  from '@/domains/Offers/components/OfferCard/OfferCharacteristicsSection.vue';

export default defineComponent({
  name: 'TenderBidCard',

  components: {
    CreateTenderBidPrimary,
    CreateTenderBidSecondary,
    OfferCharacteristicSection,
  },

  props: {
    /** Существующая ставка — для просмотра и редактирования. */
    tenderBid: {
      type: Object as PropType<TenderBid | null>,
      default: null,
    },

    /**
     * Идентификатор офера, из которого собирается новая ставка.
     * Сам офер догружается в init: карточка не принимает его снаружи,
     * чтобы не работать с урезанным объектом из реестра.
     */
    offerId: {
      type: Number as PropType<number | null>,
      default: null,
    },

    /** Тендер, в который делается новая ставка. Нужен только вместе с offerId. */
    tender: {
      type: Object as PropType<Tender | null>,
      default: null,
    },
  },

  emits: ['tenderBid:created', 'tenderBid:updated', 'order:created'],

  setup() {
    return {TendersService, TenderBidsService};
  },

  data() {
    const tabs = [
      {id: 'primary', name: 'Основное'},
      {id: 'secondary', name: 'Другое'},
      {id: 'characteristic', name: 'Характеристики', showAttentionDot: false},
    ];

    return {
      /** Заполняется в init на created. */
      localBid: undefined as unknown as TenderBid,
      tabs,
      activeTab: tabs[0],
    };
  },

  created() {
    this.init();
  },

  computed: {},

  methods: {
    /**
     * Ставка, с которой работает диалог.
     *
     * Существующая — клонируется: пользователь может закрыть диалог, и правки
     * не утекут в оригинал. Новая — собирается из офера, который карточка
     * грузит сама по offerId, поля тендера доставляет setTender.
     *
     * Маршрут перевозки здесь не грузится: его отдаёт источник селекта
     * в CreateTenderBidPrimary, он же запускает каскад пересчёта цен.
     */
    async init(): Promise<void> {
      /** Собираем целиком и присваиваем одним движением: в this.localBid
       *  не должно попадать недособранное состояние. */
      let localBid: TenderBid;

      if (this.tenderBid !== null) {
        localBid = this.$core.utils.cloneDeep(this.tenderBid);
      } else {
        const offer = await OffersService.get(this.offerId!);

        // Ловим ошибки неконсистетности данных, например по НДС, и сразу закрываем карточку
        try {
          localBid = TenderBid.fromOffer(offer);
          localBid.setTender(this.tender!);
        } catch (e) {
          this.$core.dialogs.TenderBidCard.hide();
        }

      }

      this.localBid = localBid;
    },

    async submit(): Promise<void> {
      if (this.localBid.isExisting === true) {
        if (await TenderBidsService.updateBid(this.localBid)) {
          this.$emit('tenderBid:updated');
          this.$core.dialogs.TenderBidCard.resolve();
        }
      } else {
        if (await TenderBidsService.addBid(this.localBid)) {
          this.$emit('tenderBid:created');
          this.$core.dialogs.TenderBidCard.resolve();
        }
      }
    },

    async openBuyDialog() {
      const isNeedToUpdate = await this.$core.dialogs.OrderCreateDialogMinimized.showAsPromise(
        {
          tenderBid: this.localBid,
          tender: this.localBid.tender,
          showOrderType: true,
        },
      );

      if (isNeedToUpdate) {
        this.$core.dialogs.TenderBidCard.resolve();
      }
    },
  },
});
</script>
